import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { mutationErrorHandler } from '@/lib/utils'
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
} from '@/lib/api/notifications'
import type { Notification } from '@/lib/api/notifications'
import {
  formatTransactionCreatedMessage,
  formatRecurringProcessedMessage,
  type TransactionCreatedData,
  type RecurringProcessedData,
} from '@/lib/notifications/format'

export type { Notification }

export function useNotifications() {
  return useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    staleTime: 2 * 60 * 1000,
  })
}

export function useUnreadNotificationCount() {
  return useQuery<number>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    staleTime: 60 * 1000,
  })
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: mutationErrorHandler('既読の更新に失敗しました'),
  })
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: mutationErrorHandler('既読の更新に失敗しました'),
  })
}

export function useNotificationSubscription() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          const notification = payload.new as Notification

          // Skip toast for recurring_processed if this client triggered it
          const justProcessed = sessionStorage.getItem('recurring-processed')
          if (notification.type === 'recurring_processed' && justProcessed) {
            sessionStorage.removeItem('recurring-processed')
          } else if (notification.type === 'transaction_created') {
            toast.info(formatTransactionCreatedMessage(notification.data as TransactionCreatedData))
          } else if (notification.type === 'recurring_processed') {
            toast.info(formatRecurringProcessedMessage(notification.data as RecurringProcessedData))
          }

          queryClient.invalidateQueries({ queryKey: ['notifications'] })
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, queryClient])
}
